import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  CompactTransaction,
  CurrentProfit,
  SingleProfitCalculationApiResponse,
  Transaction,
} from '../../../types';

import Charts from './Charts';
import Transactions from './Transactions';
import {
  findTransactionWave,
  transformToHeikinCandles,
} from '../../../../../helpers/bundleTransactionsToCompactForm';
import {
  Candle,
  CandleType,
  ComplexParamsBundle,
  LatestBotState,
} from '../../../../Detail/types';
import ProfitInformation from './ProfitInformation';
import { ComponentDimensions } from '../../../../../types/Layout';
import { TimeFrame } from '../../../../../types/Bot';
import { useGetRsiSignalsQuery } from '../../../../../store/services/api';

interface Props {
  data: SingleProfitCalculationApiResponse;
  rootDimensions?: ComponentDimensions | undefined;
}

const SingleProfitCalculationResult = (props: Props) => {
  const { data } = props;

  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >(props.rootDimensions);
  const rootRef = useRef<any>(null);

  useLayoutEffect(() => {
    if (!rootRef.current) {
      return;
    }

    if (!!rootDimensions) {
      return;
    }

    setRootDimensions({
      height: rootRef.current.clientHeight,
      width: rootRef.current.clientWidth,
    });
  }, [rootDimensions]);

  const [isChartAnimationDone, setIsChartAnimationDone] = useState(true);

  // Rsi values

  const { data: rsiDataOppositeOrder } = useGetRsiSignalsQuery({
    basicParams: {
      baseAsset: data.baseAsset,
      quoteAsset: data.quoteAsset,
      exchange: data.exchange,
      startTimestamp: new Date(data.startTimestamp).valueOf(),
      endTimestamp: new Date(data.endTimestamp).valueOf(),
    },
    rsiConfig: {
      period: String(data.indicatorConfig.oppositeOrderPeriod),
      threshold: String(data.indicatorConfig.oppositeOrderThreshold),
      candleType:
        data.indicatorConfig.oppositeOrderCandleType === 'normal'
          ? CandleType.Normal
          : CandleType.Heikin,
      timeFrame: data.indicatorConfig.oppositeOrderTimeFrame,
      childTimeFrame: data.indicatorConfig.oppositeOrderChildTimeFrame,
    },
  });

  const { data: rsiDataSameOrder } = useGetRsiSignalsQuery({
    basicParams: {
      baseAsset: data.baseAsset,
      quoteAsset: data.quoteAsset,
      exchange: data.exchange,
      startTimestamp: new Date(data.startTimestamp).valueOf(),
      endTimestamp: new Date(data.endTimestamp).valueOf(),
    },
    rsiConfig: {
      period: String(data.indicatorConfig.sameOrderPeriod),
      threshold: String(data.indicatorConfig.sameOrderThreshold),
      candleType:
        data.indicatorConfig.sameOrderCandleType === 'normal'
          ? CandleType.Normal
          : CandleType.Heikin,
      timeFrame: data.indicatorConfig.sameOrderTimeFrame,
      childTimeFrame: data.indicatorConfig.sameOrderChildTimeFrame,
    },
  });

  const shorterTHeikinCandles = useMemo(() => {
    if (!data?.savedData?.shorterTNormalCandles) {
      return [];
    }

    const shorterTNormalCandles = data?.savedData?.shorterTNormalCandles.reduce(
      (acc, curr) => {
        acc.push(...curr);
        return acc;
      },
      [],
    );

    if (!shorterTNormalCandles) {
      return [];
    }

    const shorterTHeikinCandles = transformToHeikinCandles(
      shorterTNormalCandles,
    );

    return shorterTHeikinCandles;
  }, [data?.savedData?.shorterTNormalCandles]);

  const multiTHeikinCandles = useMemo(() => {
    if (!data?.savedData?.multiOrderCandles) {
      return [];
    }

    const multiOrderHeikinCandles = data?.savedData?.multiOrderCandles.reduce<
      Candle[]
    >((acc, curr) => {
      const t = curr.reduce((acc2, curr2) => {
        acc2.push(...curr2);
        return acc2;
      }, []);

      acc.push(...[...t]);
      return acc;
    }, []);

    if (!multiOrderHeikinCandles) {
      return [];
    }

    const multiTHeikinCandles = transformToHeikinCandles(
      multiOrderHeikinCandles,
    );

    return multiTHeikinCandles;
  }, [data?.savedData?.multiOrderCandles]);

  const setCandles = useCallback(
    (
      transaction: Transaction | CompactTransaction,
      transactions: Transaction[],
    ) => {
      /**
       * De trade in het midden
       * Links en rechts de vorige en volgende trade (compact)
       */

      let startTimestamp, endTimestamp;
      const compactTransactionId = transaction.compactTransactionId;

      const compactTransactionsWithoutTPTs = transactions.filter(
        (t) => !t.transactionType.includes('TAKE_PROFIT'),
      );

      const compactTransactionIndex = compactTransactionsWithoutTPTs.findIndex(
        (t) => t.compactTransactionId === compactTransactionId,
      );

      if (compactTransactionIndex === 0) {
        startTimestamp = transaction.timestamp;
      } else {
        const previousCompactTransaction =
          compactTransactionsWithoutTPTs[compactTransactionIndex - 1];
        startTimestamp = previousCompactTransaction.timestamp;
      }

      if (
        compactTransactionIndex ===
        compactTransactionsWithoutTPTs.length - 1
      ) {
        endTimestamp = new Date().valueOf();
      } else {
        const nextCompactTransaction =
          compactTransactionsWithoutTPTs[compactTransactionIndex + 1];

        endTimestamp = nextCompactTransaction.timestamp;
      }

      const higherTransactionWave = findTransactionWave(
        data.savedData.higherTHeikinCandles,
        startTimestamp,
        endTimestamp,
      );

      if (!higherTransactionWave?.length) {
        return;
      }

      const start = higherTransactionWave[0].startTimestamp;
      const end =
        higherTransactionWave[higherTransactionWave.length - 1].endTimestamp;

      transaction.candles = {
        higher: higherTransactionWave,
        shorter: shorterTHeikinCandles.filter(
          (c) => start <= c.startTimestamp && c.endTimestamp <= end,
        ),
        multi: multiTHeikinCandles.filter(
          (c) => start <= c.startTimestamp && c.endTimestamp <= end,
        ),
      };
    },
    [data, multiTHeikinCandles, shorterTHeikinCandles],
  );

  const setCandlesTakeProfit = useCallback(
    (
      transaction: Transaction | CompactTransaction,
      transactions: Transaction[],
    ) => {
      const compactTransactionsWithoutTPTs = transactions.filter(
        (t) => !t.transactionType.includes('TAKE_PROFIT'),
      );

      // Set candles from previous compact transaction to the next one
      const transactionTimestamp = transaction.timestamp;
      const closestCompactTransactionIndex = parseInt(
        Object.entries(
          compactTransactionsWithoutTPTs.map((ct) =>
            Math.abs(transactionTimestamp - ct.timestamp),
          ),
        ).sort((a, b) => a[1] - b[1])[0][0],
      );
      const closestCompactTransaction =
        compactTransactionsWithoutTPTs[closestCompactTransactionIndex];

      let start: number, end: number;

      if (closestCompactTransaction.timestamp > transactionTimestamp) {
        start =
          closestCompactTransactionIndex > 0
            ? compactTransactionsWithoutTPTs[closestCompactTransactionIndex - 1]
                .timestamp
            : data.savedData.higherTHeikinCandles[0].startTimestamp;
        end = closestCompactTransaction.timestamp;
      } else {
        start = closestCompactTransaction.timestamp;
        end =
          closestCompactTransactionIndex ===
          compactTransactionsWithoutTPTs.length - 1
            ? new Date().valueOf()
            : compactTransactionsWithoutTPTs[closestCompactTransactionIndex + 1]
                .timestamp;
      }

      const higherTCandles = data.savedData.higherTHeikinCandles.filter(
        (c) =>
          start - (c.endTimestamp - c.startTimestamp) <= c.startTimestamp &&
          c.endTimestamp <= end + (c.endTimestamp - c.startTimestamp),
      );

      transaction.candles = {
        higher: higherTCandles,
        shorter: shorterTHeikinCandles.filter(
          (c) =>
            higherTCandles[0].startTimestamp <= c.startTimestamp &&
            c.endTimestamp <=
              higherTCandles[higherTCandles.length - 1].endTimestamp,
        ),
        multi: multiTHeikinCandles.filter(
          (c) =>
            higherTCandles[0].startTimestamp <= c.startTimestamp &&
            c.endTimestamp <=
              higherTCandles[higherTCandles.length - 1].endTimestamp,
        ),
      };
    },
    [data, multiTHeikinCandles, shorterTHeikinCandles],
  );

  const transactions = useMemo(() => {
    if (!data) {
      return [];
    }

    const transactions: Transaction[] = [];

    data.transactions.forEach((t) => {
      const transaction = { ...t };

      if (transaction.transactionType.includes('TAKE_PROFIT')) {
        setCandlesTakeProfit(transaction, data.transactions);
      } else {
        setCandles(transaction, data.transactions);
      }

      transactions.push(transaction);
    });

    return transactions;
  }, [data, setCandles, setCandlesTakeProfit]);

  const currentComplexParams: ComplexParamsBundle | undefined = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return {
      complexParams: {
        cbrt: data.cbrt,
        shorterTimeFrameInterval: data.shorterTimeFrameInterval,
        supportCbrtRange: data.supportCbrtRange,
        supportMrcState: data.supportMrcState,
      },
      timeFrames: {
        higher: data.higherTimeFrame as TimeFrame,
        shorter: data.shorterTimeFrame as TimeFrame,
        multiOrder: data.multiOrderTimeFrame as TimeFrame,
      },
      indicatorConfig: data.indicatorConfig,
    };
  }, [data]);

  const currentProfit: CurrentProfit | undefined = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return {
      currentProfit: data.currentProfit,
      currentPriceChange: data.currentPriceChange,
    };
  }, [data]);

  const latestBotState: LatestBotState | undefined = useMemo(() => {
    if (!data || !transactions.length) {
      return undefined;
    }

    /** We want to filter the multiT candles
     * start  of last wave (the opposite than latestOrderType) until now
     */

    const nonTPTransactions = transactions.filter(
      (t) => !t.transactionType.includes('TAKE_PROFIT'),
    );
    const latestTransaction = nonTPTransactions[nonTPTransactions.length - 1];
    const startTimestamp = latestTransaction.timestamp;

    if (!startTimestamp) {
      return undefined;
    }

    // @TODO ????
    let startOfWaveTimestamp = null;

    const candles = [...data.savedData.higherTHeikinCandles].sort(
      (a, b) => a.startTimestamp - b.startTimestamp,
    );

    for (let index = candles.length - 1; index >= 0; index--) {
      const candle = candles[index];
      const candleOrderType = candle.direction === 'up' ? 'BUY' : 'SELL';

      if (!candle.startOfNewTrend) {
        continue;
      }

      if (candleOrderType === latestTransaction?.orderType) {
        continue;
      }

      if (candle.startTimestamp < latestTransaction.timestamp) {
        break;
      }

      startOfWaveTimestamp = candle.startTimestamp;
      break;
    }

    if (!startOfWaveTimestamp) {
      startOfWaveTimestamp = latestTransaction.timestamp; // latest transaction timestamp
    }

    /** We want to filter all VISIBLE states,
     * even if the start is before the start of the wave
     * if the line is still visible, include it
     */

    const candleData =
      data.currentMrcState?.timeFrame === data.multiOrderTimeFrame
        ? multiTHeikinCandles
        : shorterTHeikinCandles;

    const oppositeOrderRsiValues =
      rsiDataOppositeOrder?.indicatorValues?.filter(
        (s) => s.timestamp >= (startTimestamp ?? 0),
      );
    const sameOrderRsiValues = rsiDataSameOrder?.indicatorValues?.filter(
      (s) => s.timestamp >= (startTimestamp ?? 0),
    );

    return {
      orderType: latestTransaction.orderType === 'BUY' ? 'SELL' : 'BUY',
      currentProfit: data.currentProfit,
      currentPriceChange: data.currentPriceChange,
      candles: candleData.filter(
        (c) => latestTransaction.timestamp <= c.startTimestamp,
      ),
      cbrtRangeStates: data.cbrtRangeStates.filter(
        (s) =>
          startTimestamp <=
          (new Date(s.startDate).valueOf() || new Date().valueOf()),
      ),
      mrcStates: data.mrcStates.filter(
        (s) => startTimestamp <= (s.startTimestamp || new Date().valueOf()),
      ),
      transactions: data.transactions.filter(
        (t) =>
          t.transactionType.includes('TAKE_PROFIT') &&
          t.timestamp > startTimestamp,
      ),
      baseAsset: data.baseAsset,
      quoteAsset: data.quoteAsset,
      timeFrame: (data.currentMrcState?.timeFrame ||
        data.multiOrderTimeFrame) as TimeFrame,
      exchange: data.exchange,
      indicatorConfig: data.indicatorConfig,
      rsiIndicatorValuesOppositeOrder: oppositeOrderRsiValues,
      rsiIndicatorValuesSameOrder: sameOrderRsiValues,
    };
  }, [
    data,
    multiTHeikinCandles,
    rsiDataOppositeOrder?.indicatorValues,
    rsiDataSameOrder?.indicatorValues,
    shorterTHeikinCandles,
    transactions,
  ]);

  const candles = useMemo(() => {
    return {
      higherT: data?.savedData.higherTNormalCandles,
      higherTHeikin: data?.savedData.higherTHeikinCandles,
      shorterT: data?.savedData.shorterTNormalCandles.flat(),
      multiT: data?.savedData.multiOrderCandles.flat(2),
    };
  }, [
    data?.savedData.higherTHeikinCandles,
    data?.savedData.higherTNormalCandles,
    data?.savedData.multiOrderCandles,
    data?.savedData.shorterTNormalCandles,
  ]);

  if (!data) {
    return null;
  }

  return (
    <div
      ref={rootRef}
      style={{
        opacity: rootDimensions ? 1 : 0,
        transition: 'opacity 500ms ease-in-out',
        paddingTop: 24,
        height: '100%',
      }}
    >
      {!!rootDimensions && (
        <>
          <Charts
            data={data}
            allCompactTransactions={transactions}
            rootDimensions={rootDimensions}
            setIsChartAnimationDone={setIsChartAnimationDone}
          />
          {isChartAnimationDone && (
            <>
              <div
                className="single-profit-calcultion-divider"
                style={{ marginTop: 48, marginBottom: 48 }}
              />
              <div
                style={{
                  border: '1px solid rgba(50, 50, 50, 1)',
                  borderRadius: 8,
                  padding: 32,
                  marginBottom: 16,
                  paddingTop: 64,
                  paddingBottom: 64,
                  flex: 1,
                  backgroundColor: 'rgba(30, 30, 30, 1)',
                  boxShadow: '4px 0px 5px rgba(255, 255, 255, 0.2)',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  width: '65%',
                }}
              >
                <ProfitInformation data={data} />
              </div>
              <div
                className="single-profit-calcultion-divider"
                style={{ marginTop: 48, marginBottom: 48 }}
              />
              <Transactions
                rootDimensions={rootDimensions}
                transactions={transactions}
                baseAsset={data.baseAsset}
                quoteAsset={data.quoteAsset}
                exchange={data.exchange}
                compactTransactionLength={transactions[0]?.id}
                allCompactTransactions={transactions}
                currentComplexParams={currentComplexParams}
                currentProfit={currentProfit}
                cbrtRangeStates={data.cbrtRangeStates}
                mrcStates={data.mrcStates}
                latestBotState={latestBotState}
                wakeupTimes={data.wakeupTimes}
                indicatorConfig={data.indicatorConfig}
                candles={candles}
                useTakeProfitTransactions={data.useTakeProfitTransactions}
                rsiIndicatorValuesOppositeOrder={
                  rsiDataOppositeOrder?.indicatorValues
                }
                rsiIndicatorValuesSameOrder={rsiDataSameOrder?.indicatorValues}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SingleProfitCalculationResult;
